import { Box, Flex, FormLabel, Switch } from "@chakra-ui/react";
import { useField, useFormikContext } from "formik";
import React from "react";
import * as AF from "src/types/formTemplate";
import { z } from "zod";
import { InputControlWithQuestionId } from "../InputControlWithQuestionId";

export const FormikSchema = z.object({
  link: z.discriminatedUnion("type", [
    z.object({
      type: z.literal("enabled"),
      url: z.string().url(),
      text: z.string().min(1, "This field is required"),
    }),
    z.object({
      type: z.literal("disabled"),
      url: z.string(),
      text: z.string(),
    }),
  ]),
});

export type FormikType = z.infer<typeof FormikSchema>;

export function toFormValue(question: AF.V2.Question): FormikType {
  return {
    link: question.link_url
      ? {
          type: "enabled",
          url: question.link_url,
          text: question.link_text ?? question.link_url,
        }
      : { type: "disabled", url: "", text: "" },
  };
}

type Props = { questionId: uuid };
export const QuestionLink: React.FC<Props> = ({ questionId }) => {
  const formik = useFormikContext<FormikType>();
  const questionLink = formik.values.link;

  if (questionLink.type === "disabled") {
    return null;
  }

  return (
    <Flex direction="column" gap="3">
      <InputControlWithQuestionId
        name="link.text"
        label="Question link text"
        questionId={questionId}
      />
      <InputControlWithQuestionId
        name="link.url"
        label="Question link URL"
        questionId={questionId}
      />
    </Flex>
  );
};

export const QuestionLinkSwitch: React.FC<Props> = ({ questionId }) => {
  const [field, , helper] = useField<FormikType["link"]["type"]>("link.type");

  const onChangeHandler = React.useCallback(async () => {
    await helper.setValue(field.value === "enabled" ? "disabled" : "enabled");
  }, [field.value, helper]);

  const id = `questionLinkSwitch-${questionId}`;
  return (
    <FormLabel htmlFor={id} display="flex" flexDirection="row" gap="3">
      <Switch
        id={id}
        isChecked={field.value === "enabled"}
        onChange={onChangeHandler}
      />
      <Box>Show link for question</Box>
    </FormLabel>
  );
};
